/**
 * useComposerConfig — read + mutate the per-conversation composer
 * configuration (mode / webSearchMode / focus /
 * selectedConnectionIds / agentModeVersionId / folderFilter).
 *
 * Sits on top of the `conversationsSlice.setConfig` action so the change
 * is bound to the active conversation. When no conversation is selected
 * the setters are no-ops and reads return the defaults — composer can
 * render its UI in either state.
 *
 * v1 ref: per-conversation config was kept inline in
 * `ConversationContext.tsx` (lines 112-116, 878-902). Audit must-not-
 * lose #3 — config persists across sidebar switches and history replay.
 *
 * May 28, 2026 — perceived-latency fix. The previous implementation
 * subscribed to the WHOLE current conversation object via
 * ``selectCurrentConversation``; any unrelated mutation on that
 * conversation (incoming message, streaming chunk, reasoning hop)
 * produced a new reference and re-rendered every consumer of this hook.
 * The route component reads ``config`` in multiple places so the click
 * on a single popover toggle cascaded into a route-wide re-render. The
 * Zustand v5 SOTA pattern is:
 *
 *   1. Subscribe **only to the config sub-object** (not the conversation).
 *   2. Wrap with ``useShallow`` so identity changes that don't touch the
 *      config fields don't re-render consumers.
 *   3. Expose **atomic single-field hooks** so a component that only
 *      reads ``webSearchMode`` doesn't re-render on ``focus`` changes.
 *
 * Refs:
 *   - Zustand discussion #2867 (atomic-selector best practice, 2026)
 *   - https://zustand.docs.pmnd.rs/reference/hooks/use-shallow
 */

import { useCallback, useMemo } from "react";
import { useShallow } from "zustand/react/shallow";

import {
  selectCurrentConversation,
  useChatStore,
} from "../state/chatStore.js";
import type { ConversationConfig } from "../state/types.js";
import { normalizeComposerFocus } from "../lib/composerFocus.js";
import { useCreateConversation } from "./useConversationMutations.js";

const DEFAULT_CONFIG: ConversationConfig = {
  mode: "fast",
  webSearchMode: false,
  focus: "standard",
  selectedConnectionIds: [],
  agentModeVersionId: null,
  folderFilter: [],
  scopedDocuments: [],
  connectSpace: null,
  terraMode: false,
};

export interface UseComposerConfigResult {
  config: ConversationConfig;
  /** True when there's no conversation selected (setters are no-ops). */
  readonly: boolean;
  set: (partial: Partial<ConversationConfig>) => void;
  reset: () => void;
}

/**
 * Internal selector — returns a stable shallow snapshot of the active
 * conversation's ``config`` plus a flag telling us if there's a current
 * conversation at all. The shape is kept flat so ``useShallow``'s
 * field-by-field comparison can skip re-renders when nothing about the
 * config has changed (even if the conversation object reference did).
 */
function selectComposerConfigSnapshot(state: ReturnType<typeof useChatStore.getState>) {
  const conv = state.currentId ? state.conversations[state.currentId] ?? null : null;
  if (!conv) {
    return {
      mode: DEFAULT_CONFIG.mode,
      webSearchMode: DEFAULT_CONFIG.webSearchMode,
      focus: DEFAULT_CONFIG.focus,
      selectedConnectionIds: DEFAULT_CONFIG.selectedConnectionIds,
      agentModeVersionId: DEFAULT_CONFIG.agentModeVersionId,
      folderFilter: DEFAULT_CONFIG.folderFilter,
      scopedDocuments: DEFAULT_CONFIG.scopedDocuments,
      connectSpace: DEFAULT_CONFIG.connectSpace,
      terraMode: DEFAULT_CONFIG.terraMode,
      tramaInfrastructure: undefined,
      conversationId: null as string | null,
    };
  }
  const cfg = conv.config ?? DEFAULT_CONFIG;
  return {
    mode: cfg.mode,
    webSearchMode: cfg.webSearchMode,
    focus: normalizeComposerFocus(cfg),
    selectedConnectionIds: cfg.selectedConnectionIds,
    agentModeVersionId: cfg.agentModeVersionId,
    folderFilter: cfg.folderFilter,
    scopedDocuments: cfg.scopedDocuments ?? DEFAULT_CONFIG.scopedDocuments,
    connectSpace: cfg.connectSpace ?? DEFAULT_CONFIG.connectSpace,
    terraMode: cfg.terraMode ?? DEFAULT_CONFIG.terraMode,
    tramaInfrastructure: cfg.tramaInfrastructure,
    conversationId: conv.id as string | null,
  };
}

export function useComposerConfig(): UseComposerConfigResult {
  // Subscribe to the config snapshot directly (NOT the whole conversation).
  // useShallow does field-level equality, so unrelated mutations on the
  // conversation (messages, streaming, etc.) don't trigger this hook's
  // consumers to re-render.
  const snapshot = useChatStore(useShallow(selectComposerConfigSnapshot));
  const setConfigAction = useChatStore((s) => s.setConfig);
  // June 3 2026 — same bootstrap path the "Nuova chat" button uses. The
  // old ``if (!snapshot.conversationId) return;`` bail-out below silently
  // dropped EVERY composer toggle (Ricerca web, Strutturati, connectors,
  // folder-filter) when the user landed on a route that didn't have a
  // pre-selected conversation (e.g. ``/workspace/documents?focus=draft_*``
  // — the document-draft entry point). Bootstrapping a real conversation
  // on first config-change makes the toggle take effect AND surfaces the
  // chat in the sidebar immediately, instead of leaving the user
  // wondering why "Ricerca web" doesn't tick on.
  const { create: createConversation } = useCreateConversation();

  const set = useCallback(
    (partial: Partial<ConversationConfig>) => {
      const convId = snapshot.conversationId ?? createConversation();
      // Read the current config so consecutive partial setters share the same scope.
      const current = useChatStore.getState().conversations[convId]?.config ?? DEFAULT_CONFIG;
      setConfigAction(convId, {
        ...partial,
        focus: normalizeComposerFocus({ ...current, ...partial }),
      });
    },
    [snapshot.conversationId, setConfigAction, createConversation],
  );

  const reset = useCallback(() => {
    // ``reset`` only makes sense for an existing conversation — wiping
    // a freshly-bootstrapped one back to defaults is a no-op anyway.
    if (!snapshot.conversationId) return;
    setConfigAction(snapshot.conversationId, DEFAULT_CONFIG);
  }, [snapshot.conversationId, setConfigAction]);

  // Re-pack into the legacy ``UseComposerConfigResult`` shape. The
  // ``config`` reference here is stable as long as none of its fields
  // changed (because ``useShallow`` returned the same snapshot).
  return useMemo<UseComposerConfigResult>(
    () => ({
      config: {
        mode: snapshot.mode,
        webSearchMode: snapshot.webSearchMode,
        focus: snapshot.focus,
        selectedConnectionIds: snapshot.selectedConnectionIds,
        agentModeVersionId: snapshot.agentModeVersionId,
        folderFilter: snapshot.folderFilter,
        scopedDocuments: snapshot.scopedDocuments,
        connectSpace: snapshot.connectSpace,
        terraMode: snapshot.terraMode,
        tramaInfrastructure: snapshot.tramaInfrastructure,
      },
      readonly: snapshot.conversationId === null,
      set,
      reset,
    }),
    [snapshot, set, reset],
  );
}

// ---------------------------------------------------------------------------
// Atomic single-field hooks (May 28, 2026)
//
// Use these in components that read ONLY one config field — they bypass
// the snapshot pack/unpack and re-render only when that single primitive
// flips. Particularly useful inside the composer where each chip cares
// about a different field (DeepModePill ↔ mode, ConnectorChip ↔
// selectedConnectionIds, etc.).
// ---------------------------------------------------------------------------

const selectActiveConfig = (
  state: ReturnType<typeof useChatStore.getState>,
): ConversationConfig | null => {
  const conv = state.currentId ? state.conversations[state.currentId] ?? null : null;
  return conv?.config ?? null;
};

export const useComposerMode = (): ConversationConfig["mode"] =>
  useChatStore((s) => selectActiveConfig(s)?.mode ?? DEFAULT_CONFIG.mode);

export const useWebSearchMode = (): boolean =>
  useChatStore((s) => selectActiveConfig(s)?.webSearchMode ?? DEFAULT_CONFIG.webSearchMode);

/**
 * Atomic read of the active Ando Connect "interroga un'area" scope (June 14,
 * 2026). ``null`` when the conversation is in normal mode. Consumed by the
 * composer chip + the area selector so they re-render only when the scope flips.
 */
export const useConnectSpaceScope = (): ConversationConfig["connectSpace"] =>
  useChatStore((s) => selectActiveConfig(s)?.connectSpace ?? null);

export const useComposerFocus = (): ConversationConfig["focus"] =>
  useChatStore((s) => normalizeComposerFocus(selectActiveConfig(s) ?? DEFAULT_CONFIG));

export const useAgentModeVersionId = (): ConversationConfig["agentModeVersionId"] =>
  useChatStore((s) => selectActiveConfig(s)?.agentModeVersionId ?? DEFAULT_CONFIG.agentModeVersionId);

// For array fields we MUST go through useShallow — returning the array
// reference directly would cause a re-render on every store mutation
// even when contents are equal.
export const useSelectedConnectionIds = (): readonly string[] =>
  useChatStore(
    useShallow(
      (s) => selectActiveConfig(s)?.selectedConnectionIds ?? DEFAULT_CONFIG.selectedConnectionIds,
    ),
  );

export const useFolderFilter = (): readonly string[] =>
  useChatStore(
    useShallow(
      (s) => selectActiveConfig(s)?.folderFilter ?? DEFAULT_CONFIG.folderFilter,
    ),
  );

export const useScopedDocuments = (): ConversationConfig["scopedDocuments"] =>
  useChatStore(
    useShallow(
      (s) => selectActiveConfig(s)?.scopedDocuments ?? DEFAULT_CONFIG.scopedDocuments,
    ),
  );

// Backwards-compat re-export — some legacy call sites still want the
// monolithic ``selectCurrentConversation`` selector for purposes other
// than config (e.g. message list rendering). Keep it exported here so
// the import path is stable.
export { selectCurrentConversation };
